import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/axios'
import { API_ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/stores/useAuthStore'
import type { OrderDetail, OrderItemDetail } from '@/api/useOrders'

export type { OrderItemDetail }

const PAGE_SIZE = 10

const KEYS = {
    all: ['admin-orders'] as const,
    list: (params?: { status?: string; payment_status?: string; search?: string; page?: number }) =>
        [...KEYS.all, 'list', params] as const,
    detail: (uuid: string) => [...KEYS.all, 'detail', uuid] as const,
}

interface PaginatedOrdersResponse {
    count: number
    next: string | null
    previous: string | null
    results: OrderDetail[]
}

interface AdminOrdersResult {
    orders: OrderDetail[]
    count: number
    totalPages: number
}

export function useAdminOrders(params?: {
    status?: string
    payment_status?: string
    search?: string
    page?: number
}) {
    const { isLogged } = useAuthStore()
    const page = params?.page ?? 1
    const offset = (page - 1) * PAGE_SIZE

    return useQuery({
        queryKey: KEYS.list(params),
        queryFn: async (): Promise<AdminOrdersResult> => {
            const searchParams = new URLSearchParams()
            searchParams.set('limit', String(PAGE_SIZE))
            searchParams.set('offset', String(offset))
            if (params?.status) searchParams.set('status', params.status)
            if (params?.payment_status) searchParams.set('payment_status', params.payment_status)
            if (params?.search) searchParams.set('search', params.search)

            const { data } = await api.get<PaginatedOrdersResponse | OrderDetail[]>(
                `${API_ROUTES.adminOrders}?${searchParams.toString()}`
            )

            if (Array.isArray(data)) {
                return {
                    orders: data,
                    count: data.length,
                    totalPages: 1,
                }
            }

            return {
                orders: data.results,
                count: data.count,
                totalPages: Math.ceil(data.count / PAGE_SIZE),
            }
        },
        enabled: isLogged,
        refetchInterval: 30_000,
    })
}

export function useUpdateOrderStatus() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ uuid, status, note, tracking_number }: { uuid: string; status: string; note?: string; tracking_number?: string }) => {
            const { data } = await api.patch<OrderDetail>(
                `${API_ROUTES.adminOrders}${uuid}/status/`,
                { status, note, tracking_number }
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.all })
        },
    })
}
